import { useEffect, useState } from "react";
import { Volume2, VolumeX, Wifi, BatteryMedium } from "lucide-react";
import { isSoundEnabled, onSoundChange, setSoundEnabled, playBeep } from "@/lib/sound";

const useClock = () => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    const time = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Jakarta",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).format(now);
    const date = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Jakarta",
        weekday: "short",
        day: "2-digit",
        month: "short",
    }).format(now);
    return { time, date };
};

export default function TopPanel({ windows, activeKey, onTaskClick, onLogoClick }) {
    const { time, date } = useClock();
    const [soundOn, setSoundOn] = useState(isSoundEnabled());
    useEffect(() => onSoundChange(setSoundOn), []);

    const toggleSound = () => {
        const next = !soundOn;
        setSoundEnabled(next);
        if (next) setTimeout(() => playBeep(880, 0.09, 0.07, "square"), 30);
    };

    return (
        <div
            data-testid="top-panel"
            className="relative z-[60] h-10 bg-[#0c0d10]/95 backdrop-blur border-b border-white/10 flex items-center px-3 gap-3 text-neutral-300 select-none"
        >
            <button
                data-testid="panel-logo"
                onClick={onLogoClick}
                className="flex items-center gap-2 font-bold text-xs tracking-tight text-white hover:text-vermilion transition-colors"
            >
                <img src="/logo.png" alt="" className="w-5 h-5 rounded" />
                wikiOS
            </button>
            <div className="hidden md:flex items-center gap-1 ml-3 overflow-x-auto">
                {windows.map((w) => (
                    <button
                        key={w.key}
                        data-testid={`panel-task-${w.key.replace(":", "-")}`}
                        onClick={() => onTaskClick(w.key)}
                        className={`font-mono text-[11px] px-2.5 py-1 rounded transition-colors whitespace-nowrap ${
                            activeKey === w.key && !w.minimized
                                ? "bg-white/15 text-white"
                                : "text-neutral-400 hover:bg-white/10 hover:text-neutral-200"
                        } ${w.minimized ? "opacity-50 italic" : ""}`}
                    >
                        {w.title}
                    </button>
                ))}
            </div>
            <div className="ml-auto flex items-center gap-3">
                <span className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[10px] text-neutral-500">
                    <Wifi size={12} className="text-neutral-400" /> jkt-core-01
                </span>
                <BatteryMedium size={14} className="hidden sm:block text-neutral-400" />
                <button
                    data-testid="sound-toggle-button"
                    onClick={toggleSound}
                    aria-label={soundOn ? "Mute sound effects" : "Unmute sound effects"}
                    className="text-neutral-400 hover:text-white transition-colors"
                >
                    {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
                </button>
                <span data-testid="panel-clock" className="font-mono text-[11px] text-neutral-300 tabular-nums">
                    <span className="hidden sm:inline">{date} · </span>{time} WIB
                </span>
            </div>
        </div>
    );
}
